




























































import { MediaVersionData, VersionData, PropertyData, Media } from '@beyondview/firebase-client-8';
import Vue, { PropType } from 'vue';
import { MetaInfo } from 'vue-meta';

import { getPreferredType } from '@/storage';
import { db, FieldValue } from '@/firebase';
import { viewerGalleryUrl } from '@/filters';

import VersionList from '@/components/VersionList.vue';
import ClipboardButton from '@/components/ClipboardButton.vue';

const properties = db.collection( 'properties' );

interface ComponentData {
	loading: boolean,
	versions: MediaVersionData[]
}

export default Vue.extend( {
	components: {
		VersionList,
		ClipboardButton
	},
	props: {
		property: {
			type: Object as PropType<PropertyData>,
			required: true
		}
	},
	data(): ComponentData {
		return {
			loading: true,
			versions: []
		};
	},
	head(): MetaInfo {
		return {
			title: `${this.$t( 'views.property.gallery.gallery' )}`
		};
	},
	computed: {
		hasMedia(): boolean | null {
			return !!this.property.defaultMediaVersionId;
		},
		defaultVersionId(): string | null {
			return this.property.defaultMediaVersionId || null;
		},
		posterFallbackSrc(): string {
			return '/images/placeholder-interior.svg';
		}
	},
	async created() {
		await this.$bind(
			'versions',
			properties
				.doc( this.$route.params.id )
				.collection( 'mediaVersions' )
				.orderBy( 'createdAt', 'desc' )
		);
		this.loading = false;
	},
	methods: {
		getVersionUrl( mediaVersion: MediaVersionData ) {
			return viewerGalleryUrl( this.property, mediaVersion.id );
		},
		getThumbSrc( mediaVersion: MediaVersionData ) {
			const { defaultItem: itemIndex, items } = mediaVersion;
			if ( !mediaVersion?.id ) {
				return this.posterFallbackSrc;
			}
			const item = items[ itemIndex ];
			if ( item.type === 'image' ) {
				const type = getPreferredType( item.sources.image.types );
				return Media.getImageUrl( {
					region: mediaVersion.region,
					propertyId: this.property.id,
					versionId: mediaVersion.id,
					type,
					// Image-specific
					source: item.sources.image,
					itemIndex
				} );
			} else if ( item.type === 'video' ) {
				const type = getPreferredType( item.sources.poster.types );
				return Media.getPosterUrl( {
					region: mediaVersion.region,
					propertyId: this.property.id,
					versionId: mediaVersion.id,
					type,
					// Poster-specific
					source: item.sources.poster,
					itemIndex
				} );
			} else {
				return [];
			}
		},
		getThumbSrcSet( mediaVersion: MediaVersionData ) {
			const { defaultItem: itemIndex, items } = mediaVersion;
			if ( !mediaVersion?.id ) {
				return this.posterFallbackSrc;
			}
			const item = items[ itemIndex ];
			if ( item.type === 'image' ) {
				const type = getPreferredType( item.sources.image.types );
				return Media.getImageUrlSet( {
					region: mediaVersion.region,
					propertyId: this.property.id,
					versionId: mediaVersion.id,
					type,
					// Image-specific
					source: item.sources.image,
					itemIndex
				} );
			} else if ( item.type === 'video' ) {
				const type = getPreferredType( item.sources.poster.types );
				return Media.getPosterUrlSet( {
					region: mediaVersion.region,
					propertyId: this.property.id,
					versionId: mediaVersion.id,
					type,
					// Poster-specific
					source: item.sources.poster,
					itemIndex
				} );
			} else {
				return [];
			}
		},
		async handlePublish( version: MediaVersionData ) {
			const mediaVersionRef = properties
				.doc( this.property.id )
				.collection( 'mediaVersions' )
				.doc( version.id );

			await this.$store.dispatch(
				'updateProperty',
				{
					propertyId: this.property.id,
					changes: { 'defaultMediaVersionId': mediaVersionRef.id }
				}
			);
			if ( version.flags.includes( VersionData.Flags.Archived ) ) {
				await this.handleRestore( version );
			}
		},
		async handleRetract( _version: MediaVersionData ) {
			await this.$store.dispatch(
				'updateProperty',
				{
					propertyId: this.property.id,
					changes: { 'defaultMediaVersionId': FieldValue.delete() }
				}
			);
		},
		async handleArchive( version: MediaVersionData ) {
			await properties
				.doc( this.property.id )
				.collection( 'mediaVersions' )
				.doc( version.id )
				.update( { flags: FieldValue.arrayUnion( VersionData.Flags.Archived ) } );
			// Also retract
			if ( this.property.defaultMediaVersionId === version.id ) {
				await this.handleRetract( version );
			}
		},
		async handleRestore( version: MediaVersionData ) {
			await properties
				.doc( this.property.id )
				.collection( 'mediaVersions' )
				.doc( version.id )
				.update( { flags: FieldValue.arrayRemove( VersionData.Flags.Archived ) } );
		},
		async handleRemove( version: MediaVersionData ) {
			await properties
				.doc( this.property.id )
				.collection( 'mediaVersions' )
				.doc( version.id )
				.delete();
		}
	}
} );
